import { useEffect, useState } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { useToast, CloudIcon, MailIcon, GlobeIcon, ChartIcon, ServiceIcon } from '../components/ui'
import { formatPlanDisplay } from '../services/planDisplay'
import ServiceDetailInline from './ServiceDetailInline'
import { modeAwareEndpoint } from '../utils/cockpitMode'
import { forbiddenText } from '../lib/forbiddenText'
import './Services.css'


interface Organization {
  id: number
  name: string
  slug: string
    public_token?: string
  member_role: string
}

interface OrgService {
  id: number
  erpnext_subscription_id: string
  plan_id: string
  plan_name: string | null
  // TQ #5466: Display nutzt item_name + variant_name (siehe planDisplay.ts).
  item_name?: string | null
  variant_name?: string | null
  status: string
  billing_interval: string
  rate: number
  currency: string
  start_date: string | null
  end_date: string | null
  linked_type: string | null
  linked_name: string | null
  // ERPNext Item.item_group — Subs mit 'Lizenzprodukte' werden im Lizenzen-Tab
  // angezeigt, nicht hier. Filter in loadServices() unten.
  item_group: string | null
}

interface Props {
  organization: Organization
}

interface PastDueInvoice {
  invoice_number: string
  outstanding_amount: number
  currency: string
  due_date: string
  stripe_hosted_url?: string | null
}

import Licenses from './Licenses'
import { isPersonalMode } from '../utils/cockpitMode'

export default function Services({ organization }: Props) {



  const { showToast } = useToast()
  const { api: keycloakApi } = useAuth()
  const [services, setServices] = useState<OrgService[]>([])
  const [pastDue, setPastDue] = useState<PastDueInvoice[]>([])
  const [message, setMessage] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // Review-Fund 07.10.2026: Der "Aktive Lizenzen"-Chevron im Dashboard
  // verlinkt auf "services#lizenzen", landet aber ohne diesen Effect nur
  // oben auf der Seite — der Lizenzen-Abschnitt (Licenses.tsx embedded,
  // id="lizenzen") ist erst nach dem Services-Inhalt und oft nicht sichtbar.
  // Gleiches Muster wie Settings.tsx (Zeile ~638), dort fuer Query-Param-
  // Tabs, hier fuer den URL-Hash direkt.
  useEffect(() => {
    const hash = window.location.hash?.slice(1)
    if (!hash) return
    const t = window.setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 150)
    return () => window.clearTimeout(t)
  }, [])

  const toggleExpand = (planId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(planId)) next.delete(planId)
      else next.add(planId)
      return next
    })
  }

  useEffect(() => {
    loadServices()
  }, [organization.slug])

  const loadServices = async () => {
    try {
      setLoading(true)
      const url = modeAwareEndpoint('services', organization)
      const response = await keycloakApi!.get(url)
      // Filter: Lizenzprodukte (G DATA, Office, Antivirus) gehören in den
      // Lizenzen-Tab, nicht hier — Source-of-Truth ist ERPNext Item.item_group.
      const all: OrgService[] = response.data.services || []
      setServices(all.filter(s => s.item_group !== 'Lizenzprodukte'))
      setPastDue(response.data.past_due_invoices || [])
      if (response.data.message) setMessage(response.data.message)
    } catch (error) {
      console.error('Failed to load services:', error)
      // TQ #960 (Patric 25.10.): ERP-403 auf Subscription still abfangen, Abo-Bereich bleibt leer, kein Toast.
      // Alle anderen Fehler behalten ihren Toast.
      if (forbiddenText(error) === null) showToast('error', 'Services konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }

  const getServiceIcon = (planId: string) => {
    const id = planId.toLowerCase()
    if (id.includes('nextcloud') || id.includes('cloud')) return <CloudIcon size={22} />
    if (id.includes('mail')) return <MailIcon size={22} />
    if (id.includes('wordpress') || id.includes('web')) return <GlobeIcon size={22} />
    if (id.includes('erp')) return <ChartIcon size={22} />
    return <ServiceIcon size={22} />
  }

  const getServiceColor = (planId: string) => {
    const id = planId.toLowerCase()
    if (id.includes('base')) return { bg: '#eff6ff', color: '#1d4ed8' }
    if (id.includes('mail')) return { bg: '#fce8ec', color: '#cc0033' }
    if (id.includes('storage')) return { bg: '#fef3c7', color: '#d97706' }
    if (id.includes('web')) return { bg: '#e8f0fd', color: '#0073aa' }
    if (id.includes('erp')) return { bg: '#e6f7e6', color: '#1a7f1a' }
    return { bg: '#f5f5f5', color: '#666' }
  }

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <div><h1>Services</h1><p className="page-subtitle">Wird geladen…</p></div>
        </div>
        <div className="services-grid">
          {[1, 2, 3].map(i => (
            <div key={i} style={{ height: 64, background: '#f9f9f9', borderRadius: 8, marginBottom: 8 }} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Services</h1>
          <p className="page-subtitle">
            {services.length === 0
              ? 'Keine aktiven Module'
              : `${services.length} aktive${services.length === 1 ? 's Modul' : ' Module'}`}
          </p>
        </div>
      </div>

      {pastDue.length > 0 && (() => {
        const total = pastDue.reduce((acc, p) => acc + p.outstanding_amount, 0)
        const currency = pastDue[0]?.currency || 'EUR'
        const totalLabel = total.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        const first = pastDue[0]
        const stripeLink = first?.stripe_hosted_url
        return (
          <div style={{
            background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8,
            padding: '12px 16px', marginBottom: '1rem', display: 'flex',
            justifyContent: 'space-between', alignItems: 'center', gap: '1rem',
            flexWrap: 'wrap',
          }}>
            <div style={{ flex: '1 1 300px' }}>
              <div style={{ fontWeight: 600, color: '#92400e', marginBottom: 4 }}>
                ⚠ {pastDue.length === 1 ? '1 Rechnung überfällig' : `${pastDue.length} Rechnungen überfällig`}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#78350f' }}>
                Module bleiben technisch aktiv. Offen: <strong>{totalLabel} {currency}</strong>
                {pastDue.length <= 3 && ' — ' + pastDue.map(p => p.invoice_number).join(', ')}
              </div>
            </div>
            {stripeLink && (
              <a href={stripeLink} target="_blank" rel="noopener noreferrer"
                style={{
                  background: '#92400e', color: 'white', padding: '8px 16px',
                  borderRadius: 6, textDecoration: 'none', fontWeight: 600,
                  whiteSpace: 'nowrap', fontSize: '0.875rem',
                }}>
                Jetzt bezahlen →
              </a>
            )}
          </div>
        )
      })()}

      {services.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon"><ServiceIcon size={56} color="#ccc" /></div>
          <h2>Keine Services vorhanden</h2>
          <p>{message || 'Für diese Organisation sind noch keine Services gebucht.'}</p>
          <div className="empty-cta">
            <a href="mailto:sales@it-sce.de?subject=Service-Anfrage" className="btn-primary">
              Service anfragen
            </a>
          </div>
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <tbody>
              {services.map((service) => {
                const colors = getServiceColor(service.plan_id)
                const displayName = formatPlanDisplay(service)
                const itemKey = `${service.erpnext_subscription_id}:${service.plan_id}`
                const isOpen = expanded.has(service.plan_id)

                return (
                  <>
                    <tr
                      key={itemKey}
                      onClick={() => toggleExpand(service.plan_id)}
                      style={{
                        borderBottom: '1px solid #f3f4f6',
                        cursor: 'pointer',
                        background: isOpen ? '#f9fafb' : '#fff',
                      }}
                      title="Details anzeigen"
                    >
                      <td style={{ padding: '0.75rem 0.5rem 0.75rem 1rem', width: 32, textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block',
                          transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.15s',
                          color: '#d1d5db', fontSize: '0.75rem',
                        }}>▶</span>
                      </td>
                      <td style={{ padding: '0.75rem 0.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 34, height: 34, borderRadius: 8,
                            background: colors.bg, color: colors.color, flexShrink: 0,
                          }}>
                            {getServiceIcon(service.plan_id)}
                          </span>
                          <div>
                            <div style={{ fontWeight: 500, color: '#1f2937' }}>{displayName}</div>
                            {service.linked_name && (
                              <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{service.linked_name}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '0.75rem 1rem 0.75rem 0.5rem', textAlign: 'right' }}>
                        {(() => {
                          const s = (service.status || 'Active').toLowerCase()
                          let bg = '#d1fae5', fg = '#065f46', label = 'Aktiv'
                          if (s === 'past due date') { bg = '#fef3c7'; fg = '#92400e'; label = 'Zahlung ausstehend' }
                          else if (s === 'trialling' || s === 'trial') { bg = '#dbeafe'; fg = '#1e3a8a'; label = 'Trial' }
                          else if (s === 'cancelled') { bg = '#fee2e2'; fg = '#7f1d1d'; label = 'Gekündigt' }
                          // TQ #6245: Backend liefert jetzt auch Unpaid/Suspended/Stopped (Blacklist statt Whitelist)
                          else if (s === 'unpaid') { bg = '#fde68a'; fg = '#92400e'; label = 'Unbezahlt' }
                          else if (s === 'suspended') { bg = '#fecaca'; fg = '#7f1d1d'; label = 'Pausiert' }
                          else if (s === 'stopped') { bg = '#e5e7eb'; fg = '#374151'; label = 'Gestoppt' }
                          return (
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: 999,
                              fontSize: '0.75rem', fontWeight: 600,
                              background: bg, color: fg,
                            }}>
                              ● {label}
                            </span>
                          )
                        })()}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr key={itemKey + ':detail'}>
                        <td colSpan={3} style={{ padding: 0, background: '#f9fafb' }}>
                          <ServiceDetailInline
                            orgSlug={organization.slug}
                            planId={service.plan_id}
                          />
                        </td>
                      </tr>
                    )}
                  </>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* Lizenzen (Drittanbieter) als Abschnitt unter Services, kein eigener Menuepunkt mehr (Org-Modus) */}
      {!isPersonalMode() && <Licenses organization={organization} embedded />}
    </div>
  )
}
